<template>
    <tailwind-layout title="Email Log Details">
        <template #header>
            <div class="px-4 sm:px-6 lg:px-8 mx-auto flex justify-between items-center">
                <div>
                    <h1 class="font-semibold text-2xl leading-tight">Email Log Details</h1>
                    <p class="text-sm text-gray-600 mt-1">{{ emailLog.subject }}</p>
                </div>
                <inertia-link :href="route('email-logs.index')" class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-500">
                    <icon name="arrow-left" class="w-4 h-4 mr-2" />
                    Back
                </inertia-link>
            </div>
        </template>
        <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg">
            <div class="px-4 py-5 sm:px-6 border-b border-gray-200">
                <div class="flex justify-between items-start">
                    <div>
                        <h3 class="text-lg leading-6 font-medium text-gray-900">Email Information</h3>
                        <p class="mt-1 max-w-2xl text-sm text-gray-500">Complete details of the email log entry</p>
                    </div>
                    <span
                        :class="[
                            'inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold',
                            emailLog.status === 'sent' ? 'bg-green-100 text-green-700' :
                            emailLog.status === 'failed' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                        ]"
                    >
                        {{ emailLog.status }}
                    </span>
                </div>
            </div>
            <div class="px-4 py-5 sm:p-6">
                <dl class="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                    <div>
                        <dt class="text-sm font-medium text-gray-500">Subject</dt>
                        <dd class="mt-1 text-sm text-gray-900">{{ emailLog.subject }}</dd>
                    </div>
                    <div>
                        <dt class="text-sm font-medium text-gray-500">From Email</dt>
                        <dd class="mt-1 text-sm text-gray-900">{{ emailLog.from_email }}</dd>
                    </div>
                    <div>
                        <dt class="text-sm font-medium text-gray-500">Recipients</dt>
                        <dd class="mt-1 text-sm text-gray-900">
                            <div v-if="Array.isArray(emailLog.recipients) && emailLog.recipients.length > 0" class="space-y-1">
                                <div v-for="(recipient, idx) in emailLog.recipients" :key="idx">
                                    {{ recipient.email || recipient }}
                                </div>
                            </div>
                            <span v-else class="text-gray-400">N/A</span>
                        </dd>
                    </div>
                    <div>
                        <dt class="text-sm font-medium text-gray-500">Mail Class</dt>
                        <dd class="mt-1 text-sm text-gray-900 font-mono text-xs">{{ emailLog.mail_class }}</dd>
                    </div>
                    <div>
                        <dt class="text-sm font-medium text-gray-500">Action Type</dt>
                        <dd class="mt-1">
                            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                  :class="emailLog.action_type === 'automatic' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'">
                                {{ emailLog.action_type === 'automatic' ? 'Automatic' : 'User Triggered' }}
                            </span>
                        </dd>
                    </div>
                    <div v-if="emailLog.triggered_by_user">
                        <dt class="text-sm font-medium text-gray-500">Triggered By</dt>
                        <dd class="mt-1 text-sm text-gray-900">
                            {{ emailLog.triggered_by_user.name }} ({{ emailLog.triggered_by_user.email }})
                        </dd>
                    </div>
                    <div v-if="emailLog.trigger_source">
                        <dt class="text-sm font-medium text-gray-500">Trigger Source</dt>
                        <dd class="mt-1 text-sm text-gray-900">{{ emailLog.trigger_source }}</dd>
                    </div>
                    <div v-if="emailLog.trigger_method">
                        <dt class="text-sm font-medium text-gray-500">Trigger Method</dt>
                        <dd class="mt-1 text-sm text-gray-900 font-mono text-xs">{{ emailLog.trigger_method }}</dd>
                    </div>
                    <div>
                        <dt class="text-sm font-medium text-gray-500">Sent At</dt>
                        <dd class="mt-1 text-sm text-gray-900">{{ emailLog.sent_at || 'Not sent yet' }}</dd>
                    </div>
                    <div>
                        <dt class="text-sm font-medium text-gray-500">Created At</dt>
                        <dd class="mt-1 text-sm text-gray-900">{{ emailLog.created_at }}</dd>
                    </div>
                    <div v-if="emailLog.ip_address">
                        <dt class="text-sm font-medium text-gray-500">IP Address</dt>
                        <dd class="mt-1 text-sm text-gray-900">{{ emailLog.ip_address }}</dd>
                    </div>
                    <div v-if="emailLog.error_message">
                        <dt class="text-sm font-medium text-red-500">Error Message</dt>
                        <dd class="mt-1 text-sm text-red-700 bg-red-50 p-3 rounded">{{ emailLog.error_message }}</dd>
                    </div>
                </dl>

                <!-- Attachments -->
                <div v-if="emailLog.attachments && emailLog.attachments.length > 0" class="mt-6 border-t border-gray-200 pt-6">
                    <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">Attachments</h3>
                    <ul class="space-y-2">
                        <li v-for="(attachment, idx) in emailLog.attachments" :key="idx" class="flex items-center space-x-2 text-sm">
                            <icon name="paper-clip" class="w-4 h-4 text-gray-400" />
                            <span class="text-gray-900">{{ attachment.name || attachment.filename || `Attachment ${idx + 1}` }}</span>
                            <span v-if="attachment.size" class="text-gray-500">({{ formatFileSize(attachment.size) }})</span>
                        </li>
                    </ul>
                </div>

                <!-- Email Content Tabs -->
                <div class="mt-6 border-t border-gray-200 pt-6">
                    <div class="border-b border-gray-200">
                        <nav class="-mb-px flex space-x-8">
                            <button
                                v-for="tab in tabs"
                                :key="tab.name"
                                @click="activeTab = tab.name"
                                :class="[
                                    activeTab === tab.name
                                        ? 'border-rent-risk-blue-500 text-rent-risk-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                                    'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm'
                                ]"
                            >
                                {{ tab.label }}
                            </button>
                        </nav>
                    </div>
                    <div class="mt-4">
                        <!-- HTML Content -->
                        <div v-show="activeTab === 'html'" class="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <div v-if="emailLog.html_content" class="prose max-w-none" v-html="emailLog.html_content"></div>
                            <div v-else class="text-gray-500 text-sm">No HTML content available</div>
                        </div>
                        <!-- Text Content -->
                        <div v-show="activeTab === 'text'" class="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <pre v-if="emailLog.text_content" class="whitespace-pre-wrap text-sm text-gray-900 font-mono">{{ emailLog.text_content }}</pre>
                            <div v-else class="text-gray-500 text-sm">No text content available</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </tailwind-layout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Icon from '@/Shared/Icon.vue'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'

const props = defineProps({
    emailLog: Object,
})

const activeTab = ref('html')
const tabs = [
    { name: 'html', label: 'HTML Content' },
    { name: 'text', label: 'Text Content' },
]

const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size'
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB'
}

onMounted(() => {
    // Default to text if no HTML content
    if (!props.emailLog.html_content && props.emailLog.text_content) {
        activeTab.value = 'text'
    }
})
</script>

